import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientAuthContext = createContext();

export const ClientAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [client, setClient] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('netblitz-client-token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/portal/clients/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setClient(data);
      } else {
        localStorage.removeItem('netblitz-client-token');
        setIsAuthenticated(false);
        setClient(null);
      }
    } catch (error) {
      console.error('Client auth check failed:', error);
      localStorage.removeItem('netblitz-client-token');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/clients/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login mislukt');
      }

      const data = await response.json();
      localStorage.setItem('netblitz-client-token', data.access_token);
      setIsAuthenticated(true);
      setClient(data.client);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('netblitz-client-token');
    setIsAuthenticated(false);
    setClient(null);
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('netblitz-client-token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, []);

  return (
    <ClientAuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      client, 
      login, 
      logout, 
      getAuthHeaders,
      checkAuth 
    }}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};
